import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import { useCreateClientUpload } from '../queries';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { QueryKeys } from '@frontend/features/common/types';

interface CreateClientUploadModalProps {
  open: boolean;
  onClose: () => void;
  agencyId: string;
}

export const CreateClientUploadModal = ({ open, onClose, agencyId }: CreateClientUploadModalProps) => {
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { mutateAsync: createClientUpload } = useCreateClientUpload({
    onSuccess: () => {
      onClose();
      setSelectedFile(null);
      queryClient.invalidateQueries([QueryKeys.getPaginatedClientUploads, agencyId]);
      addToast({
        message: t('dataUploads.clients.create.createdSuccess'),
        color: 'success',
      });
    },
    onError: () => {
      addToast({
        message: t('dataUploads.clients.create.createdError'),
        color: 'error',
      });
    },
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('agencyId', agencyId);

    try {
      await createClientUpload(formData);
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xs" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px',
          p: 1
        }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, fontSize: '1.1rem' }}>
        {t('dataUploads.clients.create.title')}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 0 }}>
        <Box sx={{ 
          textAlign: 'center', 
          py: 4,
          px: 2,
          border: '1px dashed',
          borderColor: 'primary.main',
          borderRadius: 1,
          bgcolor: (theme) => theme.palette.grey[50],
          cursor: 'pointer'
        }}>
          <Box component="label" sx={{ cursor: 'pointer', display: 'block' }}>
            <input
              type="file"
              hidden
              accept=".csv"
              onChange={handleFileSelect}
            />
            <CloudUploadIcon 
              sx={{ 
                fontSize: 40, 
                color: 'primary.main',
                opacity: 0.8
              }} 
            />
            <Box sx={{ 
              color: 'primary.main',
              mt: 1,
              fontSize: '0.875rem'
            }}>
              {selectedFile ? selectedFile.name : t('dataUploads.clients.create.description')}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button onClick={onClose} variant="text">
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={!selectedFile}
          disableElevation
        >
          {t('common.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 